/**
 * Admin: catálogo de plantillas — Sprint 12 redesign.
 *
 * Lista todas las plantillas del tenant agrupadas por las 4 categorías
 * canónicas del landing (Seguridad física / Cybersecurity / Reg. RD /
 * Aduanas e inocuidad) + 'Otras'. Cada plantilla muestra su versión
 * más reciente.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CATEGORY_META,
  type CategoryGroup,
  categoryForCode,
  groupByCategory,
  type TemplateCategory,
} from '@assessos/shared';

import { type AdminTemplateSummary, adminSurveysApi } from '../api/admin-surveys.js';
import {
  Badge,
  ButtonLink,
  Card,
  EmptyState,
  LoadingSkeleton,
  PageHeader,
} from '../components/ui.js';

// Sprint A · Historia A2 — orden visual del dropdown de filtro.
const CATEGORY_OPTIONS: TemplateCategory[] = [
  'physical_security',
  'cybersecurity',
  'regulatory_rd',
  'customs_food',
  'other',
];

interface TemplateGroup {
  code: string;
  /** Última versión (mayor v). */
  latest: AdminTemplateSummary;
  versions: AdminTemplateSummary[];
}

export function AdminSurveysListPage(): JSX.Element {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AdminTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Sprint A · Historia A2 — filtro por categoría. '' = sin filtro.
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | ''>('');

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminSurveysApi
      .list(categoryFilter ? { category: categoryFilter } : {})
      .then((r) => setRows(r.templates))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Error'))
      .finally(() => setLoading(false));
  }, [categoryFilter]);

  const grouped = useMemo<CategoryGroup<TemplateGroup>[]>(() => {
    const byCode = new Map<string, AdminTemplateSummary[]>();
    for (const t of rows) {
      const list = byCode.get(t.code) ?? [];
      list.push(t);
      byCode.set(t.code, list);
    }
    const codeGroups: TemplateGroup[] = [...byCode.entries()].map(([code, versions]) => {
      const sorted = [...versions].sort((a, b) => b.version - a.version);
      return { code, latest: sorted[0]!, versions: sorted };
    });
    return groupByCategory(codeGroups, (g) => g.code).map((cat) => ({
      ...cat,
      items: [...cat.items].sort((a, b) => a.latest.name.localeCompare(b.latest.name)),
    }));
  }, [rows]);

  const totalTemplates = rows.length === 0 ? 0 : new Set(rows.map((r) => r.code)).size;

  function badgeToneFor(code: string): 'brand' | 'emerald' | 'amber' | 'purple' | 'slate' {
    switch (categoryForCode(code)) {
      case 'physical_security':
        return 'brand';
      case 'cybersecurity':
        return 'emerald';
      case 'regulatory_rd':
        return 'amber';
      case 'customs_food':
        return 'purple';
      default:
        return 'slate';
    }
  }

  return (
    <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
      <PageHeader
        eyebrow="ADMIN"
        title="Catálogo de plantillas"
        subtitle={
          totalTemplates > 0
            ? `${totalTemplates} plantillas únicas en ${grouped.length} categorías.`
            : 'Plantillas reutilizables agrupadas por familia normativa.'
        }
        actions={
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-500" htmlFor="category-filter">
              Categoría
            </label>
            <select
              id="category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as TemplateCategory | '')}
              className="text-sm rounded border border-slate-200 px-2 py-1.5 bg-white text-brand-900 focus:border-brand-500 focus:outline-none"
            >
              <option value="">Todas</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_META[c].label}
                </option>
              ))}
            </select>
            <ButtonLink to="/admin/surveys/new" size="md">
              + Nueva plantilla
            </ButtonLink>
          </div>
        }
      />

      {error && (
        <div
          role="alert"
          className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      {loading && rows.length === 0 && <LoadingSkeleton rows={4} />}

      {!loading && rows.length === 0 && !error && (
        <EmptyState
          title="No hay plantillas todavía"
          description="Crea tu primera plantilla custom o ejecuta el seed para cargar las normas pre-cargadas (CCTV, ISO 27001, OEA, NORTIC, HACCP…)."
          cta={{ label: '+ Crear primera plantilla', to: '/admin/surveys/new' }}
        />
      )}

      {grouped.map((cat) => (
        <Card
          key={cat.category}
          title={cat.meta.label}
          hint={`${cat.items.length} ${cat.items.length === 1 ? 'plantilla' : 'plantillas'} · ${cat.meta.description}`}
        >
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cat.items.map((g) => (
              <li key={g.code}>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/surveys/${g.latest.id}/edit`)}
                  className="group w-full text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-brand-500 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <p className="font-semibold text-brand-900 leading-snug">{g.latest.name}</p>
                      <p className="text-xs font-mono text-slate-400">
                        {g.code} · v{g.latest.version}
                        {g.versions.length > 1 && (
                          <span className="ml-1 text-slate-300">
                            ({g.versions.length} versiones)
                          </span>
                        )}
                      </p>
                      {g.latest.description && (
                        <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                          {g.latest.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        <Badge tone={badgeToneFor(g.code)}>{cat.meta.short}</Badge>
                        {g.latest.is_published ? (
                          <Badge tone="emerald">Publicada</Badge>
                        ) : (
                          <Badge tone="slate">Borrador</Badge>
                        )}
                      </div>
                    </div>
                    <span
                      className="text-slate-300 group-hover:text-brand-500 shrink-0"
                      aria-hidden
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="h-5 w-5"
                      >
                        <polyline
                          points="7 4 13 10 7 16"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </main>
  );
}
